import React from 'react';
import {
  Box,
  Card,
  CircularProgress,
  Grid,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

const formatAmount = (value) => {
  if (value == null) return '₹0';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '₹0';
  return `₹${amount.toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const formatCount = (value) => {
  if (value == null) return '0';
  const count = Number(value);
  if (!Number.isFinite(count)) return '0';
  return count.toLocaleString('en-IN');
};

const STATUS_CARDS = [
  { key: 'success', label: 'Success', chip: 'Success', accent: '#16a34a' },
  { key: 'failed', label: 'Failed', chip: 'Failed', accent: '#dc2626' },
  { key: 'pending', label: 'Expired', chip: 'Expired', accent: '#d97706' },
  { key: 'expired', label: 'Pending', chip: 'Pending', accent: '#64748b' },
];

const PAYMENT_CHANNELS = [
  {
    key: 'wallet',
    label: 'Wallet',
    chip: 'Wallet',
    icon: AccountBalanceWalletOutlinedIcon,
    accent: '#4f46e5',
  },
  {
    key: 'razorpay',
    label: 'Razorpay',
    chip: 'Razorpay',
    icon: CreditCardOutlinedIcon,
    accent: '#2563eb',
  },
  {
    key: 'reward',
    label: 'Reward',
    chip: 'Reward',
    icon: CardGiftcardOutlinedIcon,
    accent: '#7c3aed',
  },
  {
    key: 'cashfree',
    label: 'Cashfree',
    chip: 'Cashfree',
    icon: PaymentsOutlinedIcon,
    accent: '#0891b2',
  },
];

const ENTRY_TYPE_CARDS = [
  {
    key: 'credits',
    label: 'Credits',
    chip: 'Credits',
    icon: TrendingUpOutlinedIcon,
    accent: '#059669',
  },
  {
    key: 'debits',
    label: 'Debits',
    chip: 'Debits',
    icon: TrendingDownOutlinedIcon,
    accent: '#dc2626',
  },
];

const AnalyticsCard = ({
  label,
  count,
  amount,
  accent = '#11409B',
  chip = 'Metric',
  icon: Icon,
  subtitle,
  metrics = [],
}) => (
  <Card
    elevation={0}
    sx={{
      width: '100%',
      height: '100%',
      minHeight: 148,
      borderRadius: '22px',
      background: `linear-gradient(145deg, ${alpha(accent, 0.08)} 0%, #ffffff 45%, ${alpha(accent, 0.04)} 100%)`,
      border: '1px solid',
      borderColor: alpha(accent, 0.14),
      boxShadow: `
        0 1px 2px ${alpha('#0f172a', 0.04)},
        0 8px 32px ${alpha(accent, 0.1)},
        inset 0 1px 0 ${alpha('#fff', 0.9)}
      `,
      position: 'relative',
      overflow: 'hidden',
      transition: 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `
          0 4px 8px ${alpha('#0f172a', 0.06)},
          0 20px 48px ${alpha(accent, 0.18)},
          inset 0 1px 0 ${alpha('#fff', 1)}
        `,
      },
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: -40,
        right: -40,
        width: 120,
        height: 120,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${alpha(accent, 0.25)} 0%, transparent 70%)`,
        opacity: 0.35,
        pointerEvents: 'none',
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        background: `linear-gradient(135deg, ${accent} 0%, ${alpha(accent, 0.7)} 100%)`,
      }}
    />
    <Box sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={2}>
        {Icon ? (
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '14px',
              background: `linear-gradient(135deg, ${accent} 0%, ${alpha(accent, 0.75)} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              boxShadow: `0 8px 24px ${alpha(accent, 0.35)}`,
            }}
          >
            <Icon sx={{ fontSize: 24 }} />
          </Box>
        ) : (
          <Box />
        )}
        <Box
          sx={{
            px: 1.25,
            py: 0.35,
            borderRadius: '8px',
            fontSize: '0.65rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: accent,
            bgcolor: alpha(accent, 0.1),
            border: `1px solid ${alpha(accent, 0.15)}`,
          }}
        >
          {chip}
        </Box>
      </Stack>

      <Typography
        sx={{
          color: '#64748b',
          fontWeight: 600,
          fontSize: '0.75rem',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          mb: 0.75,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: { xs: '1.5rem', sm: '1.75rem' },
          color: '#0f172a',
          letterSpacing: '-0.03em',
          lineHeight: 1.1,
        }}
      >
        {formatCount(count)}
      </Typography>
      <Typography sx={{ mt: 0.75, fontWeight: 700, color: accent, fontSize: '1rem' }}>
        {formatAmount(amount)}
      </Typography>

      {metrics.length > 0 && (
        <Stack
          direction="row"
          flexWrap="wrap"
          gap={0.75}
          sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${alpha(accent, 0.1)}` }}
        >
          {metrics.map(({ label: metricLabel, count: metricCount, color }) => (
            <Box
              key={metricLabel}
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: '8px',
                bgcolor: alpha(color || accent, 0.08),
                border: `1px solid ${alpha(color || accent, 0.12)}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  color: color || accent,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {metricLabel}: {formatCount(metricCount)}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}

      {subtitle && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{
            mt: 'auto',
            pt: 1.5,
            borderTop: metrics.length ? 'none' : `1px solid ${alpha(accent, 0.1)}`,
          }}
        >
          <FiberManualRecordIcon
            sx={{ fontSize: 8, color: accent, filter: `drop-shadow(0 0 4px ${accent})` }}
          />
          <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 500 }}>
            {subtitle}
          </Typography>
        </Stack>
      )}
    </Box>
  </Card>
);

const ChannelAnalyticsCard = ({ channel, data }) => {
  const Icon = channel.icon;
  if (!data) return null;

  const metrics = [
    { label: 'Success', count: data?.success?.count, color: '#16a34a' },
    { label: 'Failed', count: data?.failed?.count, color: '#dc2626' },
    { label: 'Pending', count: data?.pending?.count, color: '#d97706' },
    { label: 'Credits', count: data?.credits?.count, color: '#059669' },
    { label: 'Debits', count: data?.debits?.count, color: '#dc2626' },
  ].filter((m) => m.count != null);

  return (
    <AnalyticsCard
      label={channel.label}
      count={data?.total?.count}
      amount={data?.total?.amount}
      accent={channel.accent}
      chip={channel.chip}
      icon={Icon}
      metrics={metrics}
      subtitle="Payment channel breakdown"
    />
  );
};

const EntryTypeCard = ({ entry, data }) => {
  const Icon = entry.icon;
  if (!data) return null;

  const metrics = [
    { label: 'Success', count: data?.success?.count, color: '#16a34a' },
    { label: 'Failed', count: data?.failed?.count, color: '#dc2626' },
    { label: 'Pending', count: data?.pending?.count, color: '#d97706' },
    { label: 'Expired', count: data?.expired?.count, color: '#64748b' },
  ].filter((m) => m.count != null);

  return (
    <AnalyticsCard
      label={entry.label}
      count={data?.total?.count}
      amount={data?.total?.amount}
      accent={entry.accent}
      chip={entry.chip}
      icon={Icon}
      metrics={metrics}
      subtitle="Entry type breakdown"
    />
  );
};

const TransactionAnalytics = ({ analytics, loading }) => {
  if (loading) {
    return (
      <Card
        elevation={0}
        sx={{
          p: 4,
          borderRadius: '24px',
          border: `1px solid ${alpha('#11409B', 0.08)}`,
          bgcolor: '#fff',
          mb: 4,
        }}
      >
        <Stack alignItems="center" justifyContent="center" spacing={2}>
          <CircularProgress size={32} sx={{ color: '#11409B' }} />
          <Typography sx={{ color: '#64748b', fontWeight: 500 }}>
            Loading transaction analytics…
          </Typography>
        </Stack>
      </Card>
    );
  }

  if (!analytics) return null;

  const { overall, byStatus } = analytics;

  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={2.5}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #11409B 0%, #2563eb 100%)',
            color: '#fff',
            boxShadow: '0 4px 16px rgba(17, 64, 155, 0.3)',
          }}
        >
          <InsightsOutlinedIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em' }}
          >
            Transaction analytics
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Overall volume, status, channels & entry types
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <AnalyticsCard
            label="Overall"
            count={overall?.count}
            amount={overall?.amount}
            accent="#11409B"
            chip="All"
            subtitle="All transactions"
          />
        </Grid>
        {STATUS_CARDS.map((status) => (
          <Grid item key={status.key} xs={12} sm={6} md={4} lg={2.4}>
            <AnalyticsCard
              label={status.label}
              count={byStatus?.[status.key]?.count}
              amount={byStatus?.[status.key]?.amount}
              accent={status.accent}
              chip={status.chip}
              subtitle={`${status.label} transactions`}
            />
          </Grid>
        ))}
      </Grid>

      <Typography
        sx={{
          fontWeight: 700,
          color: '#0f172a',
          mb: 2,
          fontSize: '0.85rem',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        By payment channel
      </Typography>
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        {PAYMENT_CHANNELS.map((channel) => (
          <Grid item key={channel.key} xs={12} sm={6} lg={3}>
            <ChannelAnalyticsCard channel={channel} data={analytics?.[channel.key]} />
          </Grid>
        ))}
      </Grid>

      <Typography
        sx={{
          fontWeight: 700,
          color: '#0f172a',
          mb: 2,
          fontSize: '0.85rem',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        By entry type
      </Typography>
      <Grid container spacing={2.5}>
        {ENTRY_TYPE_CARDS.map((entry) => (
          <Grid item key={entry.key} xs={12} sm={6}>
            <EntryTypeCard entry={entry} data={analytics?.[entry.key]} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default TransactionAnalytics;
